import { DOMSerializer, DOMParser as ProseMirrorDOMParser } from 'prosemirror-model';
import { AllSelection } from 'prosemirror-state';
var blockWrappers = ['div', 'ol', 'ul', 'li', 'table', 'tbody', 'thead', 'tfoot', 'td', 'th', 'p', 'tr', 'col', 'colgroup', 'article', 'main', 'nav', 'header', 'footer', 'aside', 'section'];
/**
 * Trims the whitespace around the provided block nodes.
 *
 * @param html - Input HTML content
 * @param trimAroundTags - Block elements to which trimming will be applied.
 * Defaults to block nodes of the current default schema:
 * 'div', 'ol', 'ul', 'li', 'table', 'tbody', 'thead', 'tfoot', 'td', 'th', 'p'
 * and additional table and semantic nodes from the default Angular Editor schema:
 * 'tr', 'col', 'colgroup', 'article', 'main', 'nav', 'header', 'footer', 'aside', 'section'
 *
 * @returns The trimmed HTML content
 */

export var trimWhitespace = function trimWhitespace(html, trimAroundTags) {
  if (trimAroundTags === void 0) {
    trimAroundTags = blockWrappers;
  }

  var tags = trimAroundTags.join('|');
  return html.replace(new RegExp('\\s*(<(?:' + tags + ')(?:\\s[^>]*?)?>)', 'g'), '$1').replace(new RegExp('(<\\/(?:' + tags + ')(?:\\s[^>]*?)?>)\\s*', 'g'), '$1');
};
/**
 * Creates a DocumentFragment from the given HTML content.
 *
 * @param html
 * @returns DocumentFragment
 */

export var htmlToFragment = function htmlToFragment(html) {
  var template = document.createElement('template');
  var fragment;

  if ('content' in template) {
    template.innerHTML = html;
    fragment = template.content;
  } else {
    // Internet Explorer
    var parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    fragment = document.createDocumentFragment();
    var dom = parsedDocument.body;

    while (dom && dom.firstChild) {
      fragment.appendChild(dom.firstChild);
    }
  }

  return fragment;
};
/**
 * Creates a DocumentFragment from the given ProseMirrorNode.
 * @param doc ProseMirrorNode
 * @returns DocumentFragment
 */

export var pmDocToFragment = function pmDocToFragment(doc) {
  return DOMSerializer.fromSchema(doc.type.schema).serializeFragment(doc.content);
};
/**
 * Creates a ProseMirrorNode from the given DOM element.
 *
 * @param dom
 * @param schema
 * @param parseOptions
 * @returns ProseMirrorNode
 */

export var domToPmDoc = function domToPmDoc(dom, schema, parseOptions) {
  return ProseMirrorDOMParser.fromSchema(schema).parse(dom, parseOptions);
};
/**
 * Creates a ProseMirrorNode from the given HTML content.
 *
 * @param content - The new HTML content.
 * @param schema - The document schema.
 * @param parseOptions - ProseMirror parse options recognized by the `parse` and `parseSlice` methods.
 * @returns - New ProseMirrorNode instance.
 */

export var parseContent = function parseContent(content, schema, parseOptions) {
  var dom = htmlToFragment(content);
  return domToPmDoc(dom, schema, parseOptions);
};
/**
 * A function that serializes the Editor State content as HTML string.
 *
 * @param state - The Editor State
 * @returns - The serialized content
 */

export var getHtml = function getHtml(state) {
  var fragment = pmDocToFragment(state.doc);
  var container = document.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML;
};
/**
 * Replaces the content of the editor with a new one.
 *
 * @param content - The new HTML content.
 * @param commandName - The name of the command.
 * @param parseOptions - ProseMirror parse options recognized by the `parse` and `parseSlice` methods.
 * @returns - Command function that takes an editor `state` and `dispatch` function.
 */

export var setHtml = function setHtml(content, command, parseOptions) {
  if (command === void 0) {
    command = 'setHTML';
  }

  if (parseOptions === void 0) {
    parseOptions = {
      preserveWhitespace: 'full'
    };
  }

  return function (state, dispatch) {
    return dispatch(state.tr.setSelection(new AllSelection(state.doc)).replaceSelectionWith(parseContent(content, state.schema, parseOptions)).setMeta('commandName', command));
  };
};